export type SubscriptionAccess = {
  ownerId?: number | null;
  paidOnly?: boolean;
  accessState?: string | null;
  billingState?: string | null;
  canWrite?: boolean | null;
  trialActive?: boolean;
  trialEndsAt?: string | null;
};

export type TrialModal = "active" | "ended" | null;

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

const activeTrialCookieName = (state: SubscriptionAccess) =>
  `me_page_active_trial_shown_${state.ownerId ?? "account"}`;

const readCookie = (name: string): string | null => {
  try {
    const match = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
};

const writeCookie = (name: string, value: string, expiresAt?: string | null) => {
  try {
    const expires = expiresAt ? new Date(expiresAt) : null;
    const lifetime =
      expires && !isNaN(expires.getTime())
        ? `expires=${expires.toUTCString()}`
        : `max-age=${ONE_YEAR_SECONDS}`;
    const sameSite =
      window.location.protocol === "https:" ? "SameSite=None; Secure" : "SameSite=Lax";

    document.cookie = `${name}=${encodeURIComponent(value)}; ${lifetime}; path=/; ${sameSite}`;
  } catch {}
};

export async function loadSubscriptionAccess(signal?: AbortSignal): Promise<SubscriptionAccess | null> {
  try {
    const base =
      process.env.REACT_APP_BACKEND_ADDRESS ||
      process.env.REACT_APP_ME_PAGE_URL ||
      "";
    const response = await fetch(`${base}/api/pricing-access`, {
      credentials: "include",
      signal,
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

export function isPaymentFailed(state: SubscriptionAccess | null): boolean {
  return state?.billingState === "payment_failed";
}

export function isTrialBlocked(state: SubscriptionAccess | null): boolean {
  if (!state) return false;

  // `canWrite` is the server-side source of truth. Keep the legacy fallback
  // while older API responses without this field are still possible.
  if (typeof state.canWrite === "boolean") return !state.canWrite;
  if (!state.paidOnly) return false;

  return !state.trialActive && state.accessState === "payment_required";
}

export function getTrialModal(state: SubscriptionAccess | null): TrialModal {
  if (!state?.paidOnly) return null;
  if (isTrialBlocked(state)) return "ended";
  if (
    state.trialActive &&
    state.accessState === "trial_active" &&
    !readCookie(activeTrialCookieName(state))
  ) {
    return "active";
  }
  return null;
}

export function markActiveTrialShown(state: SubscriptionAccess) {
  writeCookie(
    activeTrialCookieName(state),
    state.trialEndsAt || "shown",
    state.trialEndsAt,
  );
}
